import { useMemo, useCallback } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/ui/DataTable'
import { HelpIcon, MaskedText, MaskToggle } from '@/components/ui'
import { PrivacySubject } from '@/models/privacy'
import { PRIVACY_HELP } from '../constants/help-content'
import {
  StatusBadge,
  VerificationBadge,
  JurisdictionBadge,
  RequestTypes,
} from './StatusBadge'
import { formatDate } from '@/lib/utils/date'

interface PrivacyTableProps {
  data: PrivacySubject[]
  isLoading?: boolean
  onView?: (subject: PrivacySubject) => void
  onRefresh?: () => void
  isRefreshing?: boolean
  toolbarLeft?: React.ReactNode
}

export function PrivacyTable({
  data,
  onView,
  onRefresh,
  isRefreshing = false,
  toolbarLeft,
}: PrivacyTableProps) {
  const columns = useMemo<ColumnDef<PrivacySubject, unknown>[]>(
    () => [
      {
        id: 'contact',
        // Include both email and phone in the accessor for global search
        accessorFn: (row) => `${row.email || ''} ${row.phone || ''} ${row.first_name || ''} ${row.last_name || ''}`,
        header: 'Contact',
        cell: ({ row }) => (
          <div className="min-w-[160px] max-w-[280px]">
            {row.original.email && (
              <div className="font-medium text-slate-900 dark:text-white truncate">
                <MaskedText type="email" value={row.original.email} />
              </div>
            )}
            {row.original.phone && (
              <div className={`text-slate-600 dark:text-slate-400 ${row.original.email ? 'text-xs' : 'font-medium text-slate-900 dark:text-white'}`}>
                <MaskedText type="phone" value={row.original.phone} />
                {!row.original.email && <span className="text-xs text-slate-500 ml-1">(phone only)</span>}
              </div>
            )}
            {!row.original.email && !row.original.phone && (
              <div className="text-slate-500">No contact</div>
            )}
            {(row.original.first_name || row.original.last_name) && (
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {row.original.first_name}{row.original.first_name && row.original.last_name && ' '}
                <MaskedText type="name" value={row.original.last_name} />
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'status_combined',
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <StatusBadge status={row.original.status} />
            <VerificationBadge status={row.original.verification_status} />
          </div>
        ),
      },
      {
        id: 'request_types',
        header: 'Requests',
        cell: ({ row }) => (
          <RequestTypes
            optOutSale={row.original.opt_out_sale}
            optOutShare={row.original.opt_out_share}
            removeData={row.original.remove_data}
            doNotContact={row.original.do_not_contact}
          />
        ),
      },
      {
        id: 'dates',
        accessorKey: 'due_date',
        header: () => (
          <div className="flex items-center gap-1">
            <span>Dates</span>
            <HelpIcon content={PRIVACY_HELP.dueDate.general} size="sm" />
          </div>
        ),
        cell: ({ row }) => {
          const dueDate = new Date(row.original.due_date)
          const isOverdue = dueDate < new Date() && row.original.status !== 'completed'
          return (
            <div className="text-sm min-w-[100px]">
              <div className="text-xs text-slate-500 dark:text-slate-400">Received</div>
              <div>{formatDate(row.original.received_at)}</div>
              <div className={`mt-1 ${isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                <span className="text-xs text-slate-500 dark:text-slate-400">Due </span>
                {formatDate(row.original.due_date)}
              </div>
            </div>
          )
        },
      },
      {
        id: 'jurisdiction',
        accessorKey: 'jurisdiction',
        header: 'Reg',
        cell: ({ row }) => <JurisdictionBadge jurisdiction={row.original.jurisdiction} />,
      },
    ],
    []
  )

  const exportPrivacyToCsv = useCallback(() => {
    const headers = [
      'id',
      'email',
      'phone',
      'first_name',
      'last_name',
      'status',
      'verification_status',
      'opt_out_sale',
      'opt_out_share',
      'remove_data',
      'do_not_contact',
      'jurisdiction',
      'date_received',
      'date_due',
      'date_modified',
      'notes',
    ]

    const formatDate = (dateStr: string | undefined): string => {
      if (!dateStr) return ''
      return dateStr.split('T')[0]
    }

    const escapeCsvCell = (value: string): string => {
      return `"${value.replace(/"/g, '""')}"`
    }

    const rows = data.map((subject) => [
      subject.id,
      subject.email || '',
      subject.phone || '',
      subject.first_name || '',
      subject.last_name || '',
      subject.status,
      subject.verification_status,
      subject.opt_out_sale,
      subject.opt_out_share,
      subject.remove_data,
      subject.do_not_contact,
      subject.jurisdiction,
      formatDate(subject.received_at),
      formatDate(subject.due_date),
      formatDate(subject.updated_at),
      subject.notes || '',
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => escapeCsvCell(String(cell))).join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `privacy-export-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }, [data])

  const mobileCard = (subject: PrivacySubject) => {
    const dueDate = new Date(subject.due_date)
    const isOverdue = dueDate < new Date() && subject.status !== 'completed'

    return (
      <div className="p-4 space-y-3">
        {/* Header: Contact + Jurisdiction */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            {subject.email && (
              <div className="font-medium text-slate-900 dark:text-white truncate">
                <MaskedText type="email" value={subject.email} />
              </div>
            )}
            {subject.phone && (
              <div className={subject.email ? 'text-sm text-slate-500 dark:text-slate-400' : 'font-medium text-slate-900 dark:text-white'}>
                <MaskedText type="phone" value={subject.phone} />
                {!subject.email && <span className="text-xs text-slate-500 ml-1">(phone only)</span>}
              </div>
            )}
            {!subject.email && !subject.phone && (
              <div className="text-slate-500">No contact</div>
            )}
            {(subject.first_name || subject.last_name) && (
              <div className="text-sm text-slate-500 dark:text-slate-400">
                {subject.first_name}{subject.first_name && subject.last_name && ' '}
                <MaskedText type="name" value={subject.last_name} />
              </div>
            )}
          </div>
          <JurisdictionBadge jurisdiction={subject.jurisdiction} />
        </div>

        {/* Status badges */}
        <div className="flex flex-wrap gap-2">
          <StatusBadge status={subject.status} />
          <VerificationBadge status={subject.verification_status} />
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-slate-500 dark:text-slate-400 text-xs">Requests</span>
            <div className="mt-1">
              <RequestTypes
                optOutSale={subject.opt_out_sale}
                optOutShare={subject.opt_out_share}
                removeData={subject.remove_data}
                doNotContact={subject.do_not_contact}
              />
            </div>
          </div>
          <div>
            <span className="text-slate-500 dark:text-slate-400 text-xs">Received</span>
            <div className="text-slate-900 dark:text-white">{formatDate(subject.received_at)}</div>
            <span className="text-slate-500 dark:text-slate-400 text-xs mt-1 block">Due</span>
            <div className={isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-900 dark:text-white'}>
              {formatDate(subject.due_date)}
              {isOverdue && <span className="ml-1 text-xs">(Overdue)</span>}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const combinedToolbar = (
    <div className="flex items-center gap-2">
      {toolbarLeft}
      <MaskToggle />
    </div>
  )

  return (
    <DataTable
      data={data}
      columns={columns}
      pageSize={25}
      enableSearch
      enableExport
      onExport={exportPrivacyToCsv}
      onRefresh={onRefresh}
      isRefreshing={isRefreshing}
      onRowClick={onView}
      emptyMessage="No privacy subjects found"
      mobileCard={mobileCard}
      toolbarLeft={combinedToolbar}
    />
  )
}
